import { DollarSign, ArrowLeftRight, ShieldCheck, Users, Clock, Layers } from 'lucide-react'
import type { ScenarioMetrics } from '../data/metricsData'
import type { ScenarioId } from '../data/scenarios'

interface MetricsDashboardProps {
  scenarioId: ScenarioId
  metrics: ScenarioMetrics
  visible: boolean
}

const RISK_COLORS: Record<ScenarioMetrics['riskLevel'], string> = {
  low: 'text-emerald-400',
  medium: 'text-amber-400',
  high: 'text-red-400',
}

const RISK_BG: Record<ScenarioMetrics['riskLevel'], string> = {
  low: 'bg-emerald-500/20 border-emerald-500/30',
  medium: 'bg-amber-500/20 border-amber-500/30',
  high: 'bg-red-500/20 border-red-500/30',
}

interface MetricTileProps {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  highlight?: boolean
  warning?: boolean
}

function MetricTile({ icon, label, value, sub, highlight, warning }: MetricTileProps) {
  return (
    <div className={`flex flex-col gap-0.5 px-5 py-3 border-r border-white/10 last:border-r-0 min-w-[120px] ${highlight ? 'text-emerald-300' : warning ? 'text-amber-300' : 'text-white'}`}>
      <div className="flex items-center gap-1.5 text-white/50 text-xs font-medium uppercase tracking-wide">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`text-2xl font-bold tabular-nums ${highlight ? 'text-emerald-300' : warning ? 'text-amber-300' : 'text-white'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-white/40">{sub}</div>}
    </div>
  )
}

export function MetricsDashboard({ metrics, scenarioId, visible }: MetricsDashboardProps) {
  if (!visible || scenarioId === 'baseline') return null

  const clearanceM = (metrics.safetyClearanceFt * 0.3048).toFixed(1)

  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-auto z-10
                    flex items-stretch rounded-xl overflow-hidden
                    bg-slate-900/85 backdrop-blur-md border border-white/10 shadow-2xl
                    transition-all duration-500">

      <MetricTile
        icon={<DollarSign className="w-3 h-3" />}
        label="Revenue"
        value={`+$${metrics.revenueImpact.toLocaleString()}`}
        sub="additional tonight"
        highlight
      />

      <MetricTile
        icon={<ArrowLeftRight className="w-3 h-3" />}
        label="Tow Moves"
        value={`${metrics.towMoves}`}
        sub="aircraft relocations"
        warning={metrics.towMoves > 5}
      />

      <MetricTile
        icon={<ShieldCheck className="w-3 h-3" />}
        label="Clearance"
        value={`${clearanceM}m`}
        sub={`${metrics.safetyClearanceFt}ft minimum`}
        warning={metrics.safetyClearanceFt < 8}
      />

      <MetricTile
        icon={<Users className="w-3 h-3" />}
        label="Crew"
        value={`${metrics.crewRequired}`}
        sub="staff required"
        warning={metrics.crewRequired > 4}
      />

      <MetricTile
        icon={<Clock className="w-3 h-3" />}
        label="Time Saved"
        value={`${metrics.timeSavingsMin} min`}
        sub="vs manual planning"
      />

      <MetricTile
        icon={<Layers className="w-3 h-3" />}
        label="Extra Spots"
        value={`+${metrics.additionalCapacity}`}
        sub="transient capacity"
        highlight={metrics.additionalCapacity > 0}
      />

      {/* Risk + rationale */}
      <div className={`flex flex-col justify-center px-5 py-3 max-w-xs border border-transparent rounded-r-xl ${RISK_BG[metrics.riskLevel]}`}>
        <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${RISK_COLORS[metrics.riskLevel]}`}>
          {metrics.riskLevel} risk
        </div>
        <p className="text-xs text-white/60 leading-snug">{metrics.rationale}</p>
      </div>
    </div>
  )
}
